import React from 'react'
import type { TFunction } from 'i18next'
import type { TaxRateData, TaxRateComparison } from '@popety-io/types'
import {
  ChartContainer,
  ChartTitle,
  RateBarContainer,
  RateBarTrack,
  RateBarSegment,
  RateBarLabel,
  RateChurchNote,
} from '../TaxSection.styles'
import { RateDistributionStrip } from '../charts/RateDistributionChart'

const formatOrdinal = (n: number): string => {
  const s = ['th', 'st', 'nd', 'rd']
  const v = n % 100
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`
}

export interface TaxRateCardProps {
  data: TaxRateData
  rateComparison?: TaxRateComparison
  t: TFunction
}

export const TaxRateCard: React.FC<TaxRateCardProps> = ({
  data,
  rateComparison,
  t,
}) => {
  const { income_rate_canton, income_rate_city } = data
  const total = income_rate_canton + income_rate_city

  const cantonPercent = total > 0 ? (income_rate_canton / total) * 100 : 0
  const cityPercent = total > 0 ? (income_rate_city / total) * 100 : 0

  // City ranking (lower rate = better rank)
  const cityRank = rateComparison?.city_rates
    ? [...rateComparison.city_rates]
        .sort((a, b) => a.rate - b.rate)
        .findIndex((d) => d.name === rateComparison.city_name) + 1
    : 0
  const cityTotal = rateComparison?.city_rates.length ?? 0

  // Church taxes
  const hasChurchTax =
    data.income_rate_religion_roman > 0 ||
    data.income_rate_religion_protestant > 0 ||
    data.income_rate_religion_christ > 0

  const churchParts: string[] = []
  if (data.income_rate_religion_roman > 0) {
    churchParts.push(
      `${t('taxRates.churchRoman')} ${data.income_rate_religion_roman}%`
    )
  }
  if (data.income_rate_religion_protestant > 0) {
    churchParts.push(
      `${t('taxRates.churchProtestant')} ${data.income_rate_religion_protestant}%`
    )
  }
  if (data.income_rate_religion_christ > 0) {
    churchParts.push(
      `${t('taxRates.churchChrist')} ${data.income_rate_religion_christ}%`
    )
  }

  return (
    <ChartContainer style={{ marginBottom: 20 }}>
      <ChartTitle>{t('taxRates.title')}</ChartTitle>

      {/* Full-width stacked bar */}
      <RateBarContainer>
        <RateBarTrack>
          <RateBarSegment
            $width={cantonPercent}
            $color="#3b82f6"
            $position="left"
          >
            <RateBarLabel>
              {t('taxRates.canton')} — {income_rate_canton}%
            </RateBarLabel>
          </RateBarSegment>
          <RateBarSegment
            $width={cityPercent}
            $color="#22c55e"
            $position="right"
          >
            <RateBarLabel>
              {t('taxRates.city')} — {income_rate_city}%
            </RateBarLabel>
          </RateBarSegment>
        </RateBarTrack>
      </RateBarContainer>

      {/* City ranking + distribution */}
      {rateComparison && rateComparison.city_rates.length > 0 && (
        <div
          style={{
            display: 'flex',
            gap: 16,
            marginTop: 4,
            paddingTop: 12,
            borderTop: '1px solid #f3f4f6',
          }}
        >
          {/* Left: rank card */}
          {cityRank > 0 && (
            <div
              style={{
                flexShrink: 0,
                width: 96,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '12px 8px',
                borderRadius: 10,
                backgroundColor: '#f0fdf4',
                border: '1px solid #dcfce7',
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 500,
                  color: '#6b7280',
                  marginBottom: 4,
                }}
              >
                {t('taxRates.cityRank')}
              </div>
              <div
                style={{
                  fontSize: 20,
                  fontWeight: 800,
                  color: '#16a34a',
                  lineHeight: 1,
                }}
              >
                {formatOrdinal(cityRank)}
              </div>
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
                {t('taxRates.ofCities', { total: cityTotal })}
              </div>
              <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 2 }}>
                {t('taxRates.inCanton', { canton: rateComparison.canton_name })}
              </div>
            </div>
          )}

          {/* Right: dot plot */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: '#374151',
                marginBottom: 4,
              }}
            >
              {t('taxRates.cityRateComparison', {
                total: cityTotal,
                canton: rateComparison.canton_name,
              })}
            </div>
            <RateDistributionStrip
              rates={rateComparison.city_rates}
              currentName={rateComparison.city_name}
              highlightColor="#22c55e"
              currentRate={income_rate_city}
            />
          </div>
        </div>
      )}

      {/* Church taxes footnote */}
      {hasChurchTax && (
        <RateChurchNote>
          {t('taxRates.churchNote')}: {churchParts.join(' · ')}
        </RateChurchNote>
      )}
    </ChartContainer>
  )
}
